import { useState, useEffect, useRef, useMemo } from "react";
import {
  Modal,
  Select,
  Stack,
  Button,
  Group,
  Paper,
  Text,
  NumberInput,
  Alert,
  Divider,
  Avatar,
  LoadingOverlay,
  SegmentedControl,
} from "@mantine/core";
import { IconAlertCircle, IconCurrencyDollar } from "@tabler/icons-react";
import {
  GetTransactionsResponse,
  TransactionResponse,
} from "../../../../../types/transactions";
import { calculateDiscountedItemPrice } from "../../../../../utils/discountUtils";
import { formatCurrency } from "../../../../../utils/cashDrawerUtils";
import { useResponsive } from "../../../../../context/ResponsiveContext";

type TipUnit = "percent" | "dollar";

const roundToCents = (value: number): number => Math.round(value * 100) / 100;

/**
 * Per-staff split for the automatic distributions. Proportionately weights each
 * staff member by their service value; when the services total is $0 (nothing
 * to weight by — e.g. a $0-priced service) it falls back to an even split so a
 * dollar tip still distributes instead of producing 0/0 = NaN. Evenly always
 * splits equally. Custom is entered manually and never routed through here.
 */
function distributeTip(
  amount: number,
  distribution: "Proportionately" | "Evenly" | "Custom",
  staff: Array<{ id: string; servicesTotal: number }>,
): Record<string, number> {
  const result: Record<string, number> = {};
  if (staff.length === 0) return result;
  const totalServiceValue = staff.reduce((sum, s) => sum + s.servicesTotal, 0);
  const useEvenSplit =
    distribution !== "Proportionately" || totalServiceValue <= 0;
  staff.forEach((s) => {
    result[s.id] = useEvenSplit
      ? amount / staff.length
      : amount * (s.servicesTotal / totalServiceValue);
  });
  return result;
}

interface TipModalProps {
  opened: boolean;
  onClose: () => void;
  transaction: GetTransactionsResponse | TransactionResponse;
  onSubmit: (data: any) => void;
  backendTips?: {
    [staffUuid: string]: {
      tip_amount: number;
      percentage_of_service_value: number;
      percentage_of_tips: number;
    };
  } | null;
}

interface FormData {
  tipAmount: number;
  distribution: "Proportionately" | "Evenly" | "Custom";
  customTips: Record<string, number>;
}

export default function TipModal({
  opened,
  onClose,
  transaction,
  onSubmit,
  backendTips = null,
}: TipModalProps) {
  const { isMobile, isTablet } = useResponsive();
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isUpdatingRef = useRef(false);

  const [formData, setFormData] = useState<FormData>({
    tipAmount: 0,
    distribution: "Proportionately",
    customTips: {},
  });
  // Operators can enter the tip as a percentage of the services total or as
  // a dollar amount; formData.tipAmount always holds the derived dollars.
  const [tipUnit, setTipUnit] = useState<TipUnit>("percent");
  const [percentValue, setPercentValue] = useState<number | string>(20);

  // Get unique staff members from services when transaction changes (memoized to prevent infinite loop)
  const staffMembers = useMemo(() => {
    return transaction.line_items.booked_services.reduce(
      (acc, service) => {
        if (
          service.staff_id &&
          !acc.find((staff) => staff.id === service.staff_id)
        ) {
          // Calculate total service value for this staff member
          const servicesTotal = transaction.line_items.booked_services
            .filter((s) => s.staff_id === service.staff_id)
            .reduce((sum, s) => sum + calculateDiscountedItemPrice(s), 0);

          // Sum up all tips for this staff member across their services
          const totalTip = transaction.line_items.booked_services
            .filter((s) => s.staff_id === service.staff_id)
            .reduce((sum, s) => sum + (s.tip || 0), 0);

          acc.push({
            id: service.staff_id,
            name: service.staff_name,
            servicesTotal,
            tip: totalTip,
          });
        }
        return acc;
      },
      [] as Array<{
        id: string;
        name: string;
        servicesTotal: number;
        tip: number;
      }>,
    );
  }, [transaction]);

  // Grand total of (discounted) service value — the base for percentage tips.
  const servicesGrandTotal = useMemo(
    () => staffMembers.reduce((sum, staff) => sum + staff.servicesTotal, 0),
    [staffMembers],
  );

  const hasExistingTips = staffMembers.some((staff) => staff.tip > 0);

  // Reset form when modal opens (only on initial open, not on transaction updates)
  useEffect(() => {
    if (!opened) return;

    // Priority 1: Use backend tips if provided (from payment polling)
    if (backendTips && Object.keys(backendTips).length > 0) {
      const customTips: Record<string, number> = {};
      let totalTip = 0;

      // Map backend tips (keyed by staff UUID) to staff members
      Object.entries(backendTips).forEach(([staffId, tipData]) => {
        customTips[staffId] = tipData.tip_amount;
        totalTip += tipData.tip_amount;
      });

      // Mark as updating so second effect doesn't interfere during initialization
      isUpdatingRef.current = true;

      setTipUnit("dollar");
      setFormData({
        tipAmount: totalTip,
        distribution: "Proportionately", // Backend calculates proportionately by default
        customTips,
      });

      // Reset the ref after a short delay
      setTimeout(() => {
        isUpdatingRef.current = false;
      }, 0);
    } else {
      // Priority 2: Check if there are existing tips on the transaction
      const totalExistingTip = staffMembers.reduce(
        (sum, staff) => sum + staff.tip,
        0,
      );

      if (totalExistingTip > 0) {
        // Existing tips found - initialize to Custom distribution with fixed amounts
        const customTips: Record<string, number> = {};
        staffMembers.forEach((staff) => {
          if (staff.tip > 0) {
            customTips[staff.id] = staff.tip;
          }
        });

        // Mark as updating so second effect doesn't interfere during initialization
        isUpdatingRef.current = true;

        setTipUnit("dollar");
        setFormData({
          tipAmount: totalExistingTip,
          distribution: "Custom",
          customTips,
        });

        // Reset the ref after a short delay
        setTimeout(() => {
          isUpdatingRef.current = false;
        }, 0);
      } else {
        // Priority 3: No existing tips — default to a 20% proportionate tip
        // the operator can adjust (as a percentage or a dollar amount).
        // Populate customTips directly (mirroring Priorities 1 & 2) so the 20%
        // default actually attaches on open instead of relying on the async
        // distribution effect below, which the isUpdatingRef guard skips on the
        // first open. A $0-services ticket has no meaningful percentage, so it
        // defaults to dollar entry.
        const defaultTip = roundToCents(servicesGrandTotal * 0.2);
        const hasServiceValue = servicesGrandTotal > 0;

        // Mark as updating so the second effect doesn't interfere during init
        isUpdatingRef.current = true;

        setTipUnit(hasServiceValue ? "percent" : "dollar");
        setPercentValue(20);
        setFormData({
          tipAmount: defaultTip,
          distribution: "Proportionately",
          customTips: distributeTip(
            defaultTip,
            "Proportionately",
            staffMembers,
          ),
        });

        // Reset the ref after a short delay
        setTimeout(() => {
          isUpdatingRef.current = false;
        }, 0);
      }
    }

    setErrors({});
  }, [opened, staffMembers, backendTips, servicesGrandTotal]);

  /** Tip entry: dollars edit tipAmount directly; percent derives it. */
  const handleTipInputChange = (value: number | string) => {
    const num = typeof value === "string" ? parseFloat(value) : value;
    const safe = Number.isNaN(num) ? 0 : num;
    if (tipUnit === "percent") {
      setPercentValue(value);
      setFormData((prev) => ({
        ...prev,
        tipAmount: roundToCents((servicesGrandTotal * safe) / 100),
      }));
    } else {
      setFormData((prev) => ({ ...prev, tipAmount: safe }));
    }
  };

  const handleTipUnitChange = (unit: string) => {
    const next = unit as TipUnit;
    if (next === tipUnit) return;
    if (next === "percent") {
      // Convert the current dollar tip into an equivalent percentage.
      const pct =
        servicesGrandTotal > 0
          ? Math.round((formData.tipAmount / servicesGrandTotal) * 1000) / 10
          : 0;
      setPercentValue(pct);
    }
    setTipUnit(next);
  };

  // Auto-calculate tip distribution when amount, type, or distribution changes
  // Only for Evenly and Proportionately distributions (not Custom)
  useEffect(() => {
    // Skip when (1) modal isn't open, (2) if Custom mode (manual tip distribution), (3) if currently updating to prevent race conditions
    if (!opened || isUpdatingRef.current || formData.distribution === "Custom")
      return;

    if (formData.tipAmount >= 0 && staffMembers.length > 0) {
      // Proportionately (with an even-split fallback when services total $0) or
      // Evenly — see distributeTip. Prevents the 0/0 = NaN that used to silently
      // zero out tips on a $0-service ticket.
      const newCustomTips = distributeTip(
        formData.tipAmount,
        formData.distribution,
        staffMembers,
      );

      // Mark as updating to prevent this effect from running again immediately
      isUpdatingRef.current = true;
      setFormData((prev) => ({ ...prev, customTips: newCustomTips }));
      setTimeout(() => {
        isUpdatingRef.current = false;
      }, 0);
    }
  }, [opened, formData.tipAmount, formData.distribution, staffMembers]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!Number.isFinite(formData.tipAmount)) {
      newErrors.tipAmount = "Please enter a valid tip amount";
    } else if (formData.tipAmount < 0) {
      newErrors.tipAmount = "Tip amount cannot be less than 0";
    }
    if (formData.distribution === "Custom") {
      const totalAllocated = Object.values(formData.customTips).reduce(
        (sum, tip) => sum + tip,
        0,
      );

      if (Math.abs(totalAllocated - formData.tipAmount) > 0.01) {
        newErrors.general = `Total allocated tip ($${totalAllocated.toFixed(2)}) must equal the total tip amount ($${formData.tipAmount.toFixed(2)})`;
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      // Assign tips to one line item per staff member (randomly select one)
      const tipAssignmentData = {
        transaction_id: transaction.transaction.id,
        line_items: {
          booked_services: [] as any[],
        },
      };

      // Group services by staff member
      const servicesByStaff = new Map<string, any[]>();
      transaction.line_items.booked_services.forEach((service) => {
        if (!servicesByStaff.has(service.staff_id)) {
          servicesByStaff.set(service.staff_id, []);
        }
        servicesByStaff.get(service.staff_id)!.push(service);
      });

      // For each staff member, randomly select one service to assign the tip
      Object.entries(formData.customTips).forEach(([staffId, tipAmount]) => {
        if (Number.isFinite(tipAmount) && tipAmount >= 0) {
          const staffServices = servicesByStaff.get(staffId) || [];
          if (staffServices.length > 0) {
            // Randomly select one service for this staff member
            const randomIndex = Math.floor(
              Math.random() * staffServices.length,
            );
            const selectedService = staffServices[randomIndex];

            tipAssignmentData.line_items.booked_services.push({
              id: selectedService.id,
              tip: tipAmount,
            });
          }
        }
      });

      // Submit the tip assignment
      await onSubmit(tipAssignmentData);
      setIsSubmitting(false);
    } catch (error) {
      setErrors({ general: "Failed to update tips. Please try again." });
      setIsSubmitting(false);
    }
  };

  const handleDistributionChange = (value: string | null) => {
    setFormData((prev) => ({
      ...prev,
      distribution:
        (value as "Proportionately" | "Evenly" | "Custom") || "Proportionately",
    }));
  };

  const handleCustomTipChange = (staffId: string, value: number | string) => {
    const numValue = typeof value === "string" ? parseFloat(value) : value;
    setFormData((prev) => ({
      ...prev,
      customTips: {
        ...prev.customTips,
        [staffId]: numValue,
      },
    }));
    setErrors((prev) => ({ ...prev, general: "" }));
  };

  const getTotalAllocated = () => {
    return (
      Object.values(formData.customTips).reduce((sum, tip) => sum + tip, 0) || 0
    );
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={hasExistingTips ? "Edit Tips" : "Add Tips"}
      size="lg"
      fullScreen={isMobile}
      centered
    >
      <LoadingOverlay visible={isSubmitting} />
      <Stack gap="lg">
        {errors.general && (
          <Alert icon={<IconAlertCircle size={16} />} title="Error" color="red">
            {errors.general}
          </Alert>
        )}

        <Group align="flex-end" gap="sm" wrap="nowrap">
          <NumberInput
            label="Tip Amount"
            required
            value={tipUnit === "percent" ? percentValue : formData.tipAmount}
            onChange={handleTipInputChange}
            error={errors.tipAmount}
            hideControls
            min={0}
            decimalScale={2}
            leftSection={tipUnit === "dollar" ? "$" : undefined}
            rightSection={tipUnit === "percent" ? "%" : undefined}
            style={{ flex: 1 }}
            description={
              tipUnit === "percent"
                ? `${formatCurrency(formData.tipAmount)} of ${formatCurrency(
                    servicesGrandTotal,
                  )} in services`
                : undefined
            }
          />
          <SegmentedControl
            value={tipUnit}
            onChange={handleTipUnitChange}
            data={[
              // Percent-of-$0 can only ever be $0, so keep the operator in
              // dollar mode when the services total is $0.
              {
                label: "%",
                value: "percent",
                disabled: servicesGrandTotal <= 0,
              },
              { label: "$", value: "dollar" },
            ]}
          />
        </Group>

        <Select
          label="Distribution Method"
          required
          data={[
            {
              value: "Proportionately",
              label: "Proportionately (by service value)",
            },
            { value: "Evenly", label: "Evenly (among staff members)" },
            { value: "Custom", label: "Custom Allocation" },
          ]}
          value={formData.distribution}
          onChange={handleDistributionChange}
        />

        <Divider />

        {formData.tipAmount >= 0 && staffMembers.length > 0 && (
          <Paper p={isMobile ? "xs" : "md"} withBorder>
            <Group justify="space-between" mb="md">
              <Text fw={500}>Staff Tips</Text>
            </Group>

            <Stack gap="md">
              {staffMembers.map((staff) => (
                <Paper
                  key={staff.id}
                  p={isMobile ? "sm" : "md"}
                  withBorder={!isMobile}
                  bg="gray.0"
                >
                  <Group justify="space-between" align="flex-start">
                    <Group gap="sm">
                      <Avatar
                        size="md"
                        color="var(--brand-teal)"
                        radius="xl"
                        display={isMobile ? "none" : "block"}
                      >
                        {staff.name.charAt(0).toUpperCase()}
                      </Avatar>
                      <Stack gap={isMobile ? "0" : "xs"}>
                        <Text fw={500}>{staff.name}</Text>
                        <Text size="sm" c="dimmed">
                          Services Total: ${staff.servicesTotal.toFixed(2)}
                        </Text>
                      </Stack>
                    </Group>

                    <NumberInput
                      value={formData.customTips[staff.id]?.toFixed(2) || 0}
                      onChange={(value) =>
                        handleCustomTipChange(staff.id, value)
                      }
                      hideControls
                      disabled={formData.distribution !== "Custom"}
                      leftSection="$"
                      styles={{
                        input: {
                          width: "100px",
                        },
                      }}
                    />
                  </Group>
                </Paper>
              ))}
            </Stack>

            {formData.distribution === "Custom" && (
              <Paper
                p={isMobile ? "sm" : "md"}
                withBorder={!isMobile}
                bg="blue.0"
                mt="md"
              >
                <Group gap={8} justify="center">
                  <Text
                    size="lg"
                    fw={500}
                    c={
                      getTotalAllocated() === formData.tipAmount
                        ? "green"
                        : "red"
                    }
                  >
                    ${getTotalAllocated().toFixed(2)}
                  </Text>
                  <Text size="lg">allocated out of</Text>
                  <Text size="lg">${formData.tipAmount.toFixed(2)}</Text>
                </Group>
              </Paper>
            )}
          </Paper>
        )}

        <Group justify="flex-end" gap="sm">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            loading={isSubmitting}
            disabled={
              formData.tipAmount === undefined ||
              formData.tipAmount < 0 ||
              (formData.distribution === "Custom" &&
                getTotalAllocated() !== formData.tipAmount)
            }
            leftSection={<IconCurrencyDollar size={16} />}
          >
            {hasExistingTips ? "Update Tip" : "Add Tip"}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
